import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Maximize2, Minimize2, MoreHorizontal, X } from 'lucide-react';
import { PHONE_QUERY, ResizeHandle, useMediaQuery } from '@bastion/ui';
import { ChatView } from '../ChatView';
import {
  useChat,
  useConversations,
  useConversationSession,
  useDeleteConversation,
  useRenameConversation,
} from '../hooks';
import { useAssistantPanelStore } from '../stores/chatStore';
import { useVisualViewportBox } from './useVisualViewportBox';
import styles from './AssistantPanel.module.css';

/** The width of the desktop app's side panel. */
const ASSISTANT_DEFAULT_WIDTH = 340;
const ASSISTANT_MIN_WIDTH = 280;
const CHAT_TAB_ID = 'chat';
/** A page's Details tab. It comes first, before chat. */
const DETAILS_TAB_ID = 'details';
const NO_TABS: AssistantPanelTab[] = [];
/**
 * The widest right panel. A drag stops here, and the wide panel of the
 * webapp (`toggleWide`) is at most this wide.
 */
export const ASSISTANT_MAX_WIDTH = 960;
const ASSISTANT_SNAP_THRESHOLD = 200;
/**
 * Where the panel covers the page when `overlayBelowWide` is set: a sheet on
 * a phone, and a drawer on a touch screen from 761px to 1099.98px (design
 * system README, "Internal apps (Bastion)"). A mouse keeps the inline column
 * at 761px and up, so a narrow desktop window does not change.
 */
export const PANEL_COVERS_PAGE_QUERY =
  '(max-width: 760px), (pointer: coarse) and (max-width: 1099.98px)';

export interface AssistantPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  width: number | null;
  onWidthChange: (width: number | null) => void;
  isResizing: boolean;
  onResizingChange: (resizing: boolean) => void;
  getAppState?: () => Record<string, unknown>;
  /** When true, a saved dashboard is open and its definition is included in chat app state. */
  dashboardAssistantActive?: boolean;
  /** Dashboard title for tooltip when `dashboardAssistantActive`. */
  dashboardAssistantLabel?: string | null;
  /**
   * When set (e.g. after confirming assistant-driven navigation), load this
   * conversation in the panel then invoke `onHandoffConsumed`.
   */
  handoffSessionId?: string | null;
  onHandoffConsumed?: () => void;
  onViewAllChats?: () => void;
  /**
   * The page's own tabs. A "details" tab comes first, then chat, then the
   * others in the order given.
   */
  extraTabs?: AssistantPanelTab[];
  /**
   * False leaves the chat tab out, as on the episode page, whose panel holds
   * the episode's Details. A chat handed to the panel then waits for a page
   * with chat.
   */
  showChat?: boolean;
  activeTabId?: string;
  onActiveTabChange?: (tabId: string) => void;
  /** True while the panel has its wide width. */
  isWide?: boolean;
  /** When set, the tab row has a button that widens or narrows the panel. */
  onToggleWide?: () => void;
  /**
   * Where PANEL_COVERS_PAGE_QUERY matches, the panel is a layer over the
   * page: a sheet at 760px and under, and a drawer on a touch screen from
   * 761px to 1099.98px. The panel then has no resize handle and no wide
   * button. The webapp sets it. The desktop app keeps the inline panel at
   * every width.
   */
  overlayBelowWide?: boolean;
}

export interface AssistantPanelTab {
  id: string;
  label: string;
  content: unknown;
  /** A short title line at the top of the tab, over the content. */
  title?: unknown;
  /** Small buttons at the right of the title line. */
  actions?: unknown;
}

/** The tab that a key moves to in a row of `count` tabs, or null. */
function nextTabIndex(key: string, index: number, count: number): number | null {
  if (key === 'ArrowRight') return (index + 1) % count;
  if (key === 'ArrowLeft') return (index + count - 1) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}

export function AssistantPanel({
  isOpen,
  onToggle,
  width,
  onWidthChange,
  isResizing,
  onResizingChange,
  getAppState,
  dashboardAssistantActive = false,
  dashboardAssistantLabel = null,
  handoffSessionId = null,
  onHandoffConsumed,
  onViewAllChats,
  extraTabs = NO_TABS,
  showChat = true,
  activeTabId = CHAT_TAB_ID,
  onActiveTabChange,
  isWide = false,
  onToggleWide,
  overlayBelowWide = false,
}: AssistantPanelProps) {
  const [panelSessionId, setPanelSessionId] = useState<string | undefined>(undefined);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const idBase = useId();
  const isPhone = useMediaQuery(PHONE_QUERY);
  const coversPage = useMediaQuery(PANEL_COVERS_PAGE_QUERY);
  // A drag resizes only the inline panel, and only the inline panel can be
  // wide. The handle measures its parent, so it is not rendered where the
  // panel is a drawer or a sheet.
  const isInline = !overlayBelowWide || !coversPage;

  useVisualViewportBox(panelRef, isOpen && isPhone);

  const {
    messages,
    isStreaming,
    error,
    sessionId: chatSessionId,
    sendMessage,
    stopStreaming,
    clearMessages,
    confirmToolCall,
    cancelToolCall,
    answerToolCall,
  } = useChat(getAppState, useAssistantPanelStore);

  const { data: conversations = [] } = useConversations();
  const renameConversation = useRenameConversation();
  const deleteConversation = useDeleteConversation();

  useConversationSession({
    sessionId: panelSessionId,
    sendMessage,
    store: useAssistantPanelStore,
  });

  // A chat handed to the panel shows on the chat tab.
  useEffect(() => {
    if (!isOpen || !handoffSessionId || !showChat) return;
    setPanelSessionId(handoffSessionId);
    onActiveTabChange?.(CHAT_TAB_ID);
    onHandoffConsumed?.();
  }, [isOpen, handoffSessionId, onActiveTabChange, onHandoffConsumed, showChat]);

  useEffect(() => {
    if (!menuOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    // Escape closes only the menu. The handled event tells the app not to
    // close the panel too.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  const handleResize = useCallback(
    (newWidth: number) => {
      if (newWidth < ASSISTANT_SNAP_THRESHOLD) {
        onToggle();
        onWidthChange(null);
      } else {
        onWidthChange(newWidth);
      }
    },
    [onToggle, onWidthChange],
  );

  const handleResizeStart = useCallback(() => {
    onResizingChange(true);
  }, [onResizingChange]);

  const handleResizeEnd = useCallback((_width: number) => {
    onResizingChange(false);
  }, [onResizingChange]);

  const handleDoubleClick = useCallback(() => {
    onWidthChange(null);
  }, [onWidthChange]);

  const handleClearConversation = useCallback(() => {
    stopStreaming();
    setPanelSessionId(undefined);
    clearMessages();
  }, [clearMessages, stopStreaming]);

  const activeSessionId = panelSessionId ?? chatSessionId;
  const activeConversation = useMemo(
    () => conversations.find((conversation) => conversation.session_id === activeSessionId) ?? null,
    [activeSessionId, conversations],
  );
  const canModifyActiveConversation = Boolean(activeConversation);
  // Without chat, a tab the page does not have shows Details or the first tab.
  const activeExtraTab =
    extraTabs.find((tab) => tab.id === activeTabId) ??
    (showChat
      ? null
      : (extraTabs.find((tab) => tab.id === DETAILS_TAB_ID) ?? extraTabs[0] ?? null));
  const selectedTabId = activeExtraTab ? activeExtraTab.id : CHAT_TAB_ID;
  // Details first, then chat, then the page's other tabs.
  const tabs = useMemo(() => {
    const pageTabs = extraTabs.map(({ id, label }) => ({ id, label }));
    const details = pageTabs.filter((tab) => tab.id === DETAILS_TAB_ID);
    const others = pageTabs.filter((tab) => tab.id !== DETAILS_TAB_ID);
    const chat = showChat ? [{ id: CHAT_TAB_ID, label: 'Chat' }] : [];
    return [...details, ...chat, ...others];
  }, [extraTabs, showChat]);

  // The chat menu belongs to the chat tab. A page that opens another tab,
  // or closes the panel, also closes the menu.
  useEffect(() => {
    if (selectedTabId !== CHAT_TAB_ID || !isOpen) setMenuOpen(false);
  }, [isOpen, selectedTabId]);

  const tabDomId = (tabId: string) => `${idBase}-tab-${tabId}`;
  const tabPanelId = `${idBase}-tabpanel`;

  const selectTab = useCallback(
    (tabId: string) => {
      setMenuOpen(false);
      onActiveTabChange?.(tabId);
    },
    [onActiveTabChange],
  );

  // Arrow keys, Home and End move along the tabs and open the tab they reach.
  const handleTabKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const nextIndex = nextTabIndex(event.key, index, tabs.length);
    if (nextIndex === null) return;
    event.preventDefault();
    event.stopPropagation();
    const next = tabs[nextIndex];
    selectTab(next.id);
    document.getElementById(tabDomId(next.id))?.focus();
  };

  const handleRenameConversation = useCallback(() => {
    setMenuOpen(false);
    if (!activeSessionId || !activeConversation) return;
    const currentTitle = activeConversation?.title?.trim() ?? '';
    const next = window.prompt('Rename chat', currentTitle);
    const title = next?.trim();
    if (!title || title === currentTitle) return;
    renameConversation.mutate({ sessionId: activeSessionId, title });
  }, [activeConversation, activeSessionId, renameConversation]);

  const handleDeleteConversation = useCallback(() => {
    setMenuOpen(false);
    if (!activeSessionId || !activeConversation) return;
    if (!window.confirm('Delete this chat? This cannot be undone.')) return;
    deleteConversation.mutate(activeSessionId, {
      onSuccess: handleClearConversation,
    });
  }, [activeConversation, activeSessionId, deleteConversation, handleClearConversation]);

  const handleViewAllChats = useCallback(() => {
    setMenuOpen(false);
    onViewAllChats?.();
  }, [onViewAllChats]);

  const sidebarClasses = [
    styles.sidebar,
    isOpen ? styles.expanded : styles.collapsed,
    isResizing ? styles.resizing : '',
  ]
    .filter(Boolean)
    .join(' ');

  const expandedWidth = width ?? ASSISTANT_DEFAULT_WIDTH;
  const inlineStyle: React.CSSProperties | undefined =
    isOpen ? { width: expandedWidth } : undefined;

  const innerStyle: React.CSSProperties = {
    minWidth: expandedWidth,
    width: expandedWidth,
  };

  const layout = overlayBelowWide ? 'drawer' : undefined;

  const wideButton =
    onToggleWide && isInline ? (
      <button
        type="button"
        className={styles.actionButton}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onToggleWide}
        aria-pressed={isWide}
        aria-label={isWide ? 'Narrow the panel' : 'Widen the panel'}
        title={isWide ? 'Narrow the panel' : 'Widen the panel'}
        data-testid="panel-wide"
      >
        {isWide ? (
          <Minimize2 className={styles.actionIcon} aria-hidden="true" />
        ) : (
          <Maximize2 className={styles.actionIcon} aria-hidden="true" />
        )}
      </button>
    ) : null;

  const closeButton = (
    <button
      type="button"
      className={`${styles.actionButton} ${styles.closeButton}`}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onToggle}
      title="Close panel"
      aria-label="Close panel"
      data-testid="panel-close"
    >
      <X className={styles.actionIcon} aria-hidden="true" />
    </button>
  );

  const hasTitleLine = Boolean(activeExtraTab && (activeExtraTab.title || activeExtraTab.actions));

  return (
    <>
      {/* Mobile backdrop when open */}
      {isOpen && (
        <div
          className={styles.mobileBackdrop}
          data-layout={layout}
          onClick={onToggle}
          aria-hidden="true"
        />
      )}
      <aside ref={panelRef} className={sidebarClasses} style={inlineStyle} data-layout={layout}>
      {isOpen ? (
        <div className={styles.panelInner} style={innerStyle}>
          {/* One row: the tabs, then the panel's own buttons at the right. */}
          <div className={styles.tabRow}>
            <div className={styles.tabList} role="tablist" aria-label="Right panel">
              {tabs.map((tab, index) => {
                const selected = tab.id === selectedTabId;
                return (
                  <button
                    key={tab.id}
                    id={tabDomId(tab.id)}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    aria-controls={tabPanelId}
                    tabIndex={selected ? 0 : -1}
                    className={styles.tabButton}
                    // A click keeps the focus where it was, so the arrow keys
                    // still move through the page's list.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => selectTab(tab.id)}
                    onKeyDown={(event) => handleTabKeyDown(event, index)}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <div className={styles.rowActions} ref={menuRef}>
              {activeExtraTab ? null : (
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={() => setMenuOpen((open) => !open)}
                  title="Chat actions"
                  aria-label="Chat actions"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  data-testid="panel-chat-actions"
                >
                  <MoreHorizontal className={styles.actionIcon} aria-hidden="true" />
                </button>
              )}
              {wideButton}
              {closeButton}
              {menuOpen && !activeExtraTab ? (
                <div className={styles.menu} role="menu">
                  <button
                    type="button"
                    className={styles.menuItem}
                    role="menuitem"
                    onClick={handleRenameConversation}
                    disabled={!canModifyActiveConversation}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    className={[styles.menuItem, styles.menuItemDanger].join(' ')}
                    role="menuitem"
                    onClick={handleDeleteConversation}
                    disabled={!canModifyActiveConversation}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className={styles.menuItem}
                    role="menuitem"
                    onClick={handleViewAllChats}
                  >
                    View all chats
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {activeExtraTab ? (
            <div
              id={tabPanelId}
              className={styles.tabBody}
              role="tabpanel"
              aria-labelledby={tabDomId(activeExtraTab.id)}
            >
              {hasTitleLine ? (
                <div className={styles.titleLine}>
                  <div className={styles.title}>{activeExtraTab.title as never}</div>
                  {activeExtraTab.actions ? (
                    <div className={styles.titleActions}>{activeExtraTab.actions as never}</div>
                  ) : null}
                </div>
              ) : null}
              <div className={styles.tabContent}>{activeExtraTab.content as never}</div>
            </div>
          ) : (
            <div
              id={tabPanelId}
              className={styles.chatBody}
              role="tabpanel"
              aria-labelledby={tabDomId(CHAT_TAB_ID)}
            >
              <ChatView
                className={styles.chat}
                compact
                messages={messages}
                isStreaming={isStreaming}
                error={error}
                onSendMessage={sendMessage}
                onStopStreaming={stopStreaming}
                onConfirmToolCall={confirmToolCall}
                onCancelToolCall={cancelToolCall}
                onAnswerToolCall={answerToolCall}
                inputPlaceholder="Message..."
                emptyStateDescription="Ask about your data, workflows, or next action."
                dashboardContextActive={dashboardAssistantActive}
                dashboardContextTitle={dashboardAssistantLabel}
              />
            </div>
          )}

          {isInline ? (
            <ResizeHandle
              side="left"
              onResize={handleResize}
              onResizeStart={handleResizeStart}
              onResizeEnd={handleResizeEnd}
              onDoubleClick={handleDoubleClick}
              minWidth={ASSISTANT_MIN_WIDTH}
              maxWidth={ASSISTANT_MAX_WIDTH}
            />
          ) : null}
        </div>
      ) : null}
    </aside>
    </>
  );
}
